import React, { useEffect, useId } from 'react';
import Button from '~/components/Button';
import Skeleton from '~/components/Loading/Skeleton';
import RiskThreshold, { type RiskThresholdItem } from '~/components/RiskThreshold';
import ConnectTaskManager from '~/components/TaskManager/ConnectTaskManager';
import { Checkbox } from '~/components/ui/checkbox';
import { cn } from '~/lib/utils';
import { api, type RouterInputs } from '~/utils/api';

import { ArrowLeftIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { RemoteTaskManagerProvider, RiskLevel, TaskManagerType } from '@prisma/client';

type UpdateScannerJobInput = RouterInputs['scanner']['updateScannerJob'];

type ActionSettingsProps = {
  input: Partial<UpdateScannerJobInput>;
  setInput: React.Dispatch<React.SetStateAction<Partial<UpdateScannerJobInput>>>;
  onRequestDetails: () => void;
  isReadOnly?: boolean;
};

// TODO: Add support for other providers
const PROVIDER = RemoteTaskManagerProvider.ASANA;

export default function ActionSettings(props: ActionSettingsProps) {
  const actionCheckboxId = useId();
  const emptyStateHeadingId = useId();
  const riskHeadingId = useId();
  const { isLoading: isProviderConnectedStatusLoading, data: providerConnectedStatus } =
    api.oAuth.getProviderConnectedStatus.useQuery(undefined, {
      refetchOnWindowFocus: false,
      enabled: !!props.input.documentEvaluationTypeId,
    });

  const { data: allDocumentEvaluationTypes } =
    api.policyEngine.getAllDocumentEvaluationTypes.useQuery();

  const evalType = allDocumentEvaluationTypes?.documentEvaluationTypes.find(
    (i) => i.id === props.input.documentEvaluationTypeId,
  );

  const isAsanaConnected = !!providerConnectedStatus?.ASANA?.connected;

  const onChangeHandler = (value: Partial<UpdateScannerJobInput>) => {
    props.setInput((prev) => ({
      ...(prev || {}),
      ...value,
    }));
  };

  const initThresholds = () => {
    const thresholds: RiskThresholdItem[] = [];
    evalType?.documentEvaluationTypePolicyGroup.forEach((i) => {
      const policyGroup = allDocumentEvaluationTypes?.policyGroups.find(
        (g) => g.id === i.policyGroupId,
      );

      policyGroup?.policies.forEach((p) => {
        thresholds.push({
          policyId: p.id,
          riskLevel: RiskLevel.LOW,
          count: 0,
        });
      });
    });

    return thresholds;
  };

  useEffect(() => {
    onChangeHandler({ autoCreateTaskProvider: PROVIDER });
  }, []);

  useEffect(() => {
    // Only initialize thresholds if they don't already exist
    // This prevents overwriting loaded database values
    if (
      !props.input.autoCreateTaskThresholds ||
      props.input.autoCreateTaskThresholds.length === 0
    ) {
      onChangeHandler({ autoCreateTaskThresholds: initThresholds() });
    }
  }, [props.input.documentEvaluationTypeId]);

  if (!props.input.documentEvaluationTypeId) {
    return (
      <section
        aria-labelledby={emptyStateHeadingId}
        className="flex h-full min-h-56 w-full items-center justify-center px-4 py-10 text-center"
      >
        <div className="flex max-w-sm flex-col items-center">
          <div className="bg-muted text-muted-foreground ring-border/70 flex size-10 items-center justify-center rounded-xl ring-1">
            <ClipboardDocumentCheckIcon aria-hidden className="size-[18px]" strokeWidth={1.75} />
          </div>
          <h3
            id={emptyStateHeadingId}
            className="text-foreground mt-4 text-sm font-semibold tracking-tight"
          >
            Choose An Evaluation Type
          </h3>
          <p className="text-muted-foreground mt-1.5 text-sm leading-5">
            Actions use its policies to decide when Asana tasks are created.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-5"
            icon={<ArrowLeftIcon aria-hidden className="size-3.5" strokeWidth={2} />}
            label="Go to details"
            onClick={props.onRequestDetails}
          />
        </div>
      </section>
    );
  }

  return (
    <div className="w-full space-y-5 pb-6">
      <label
        htmlFor={actionCheckboxId}
        className={cn(
          'border-border bg-card flex w-full items-start gap-3 rounded-xl border px-4 py-3.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-colors',
          props.isReadOnly ? 'cursor-default' : 'hover:bg-muted/30 cursor-pointer',
        )}
      >
        <Checkbox
          id={actionCheckboxId}
          className="mt-0.5 size-4"
          disabled={props.isReadOnly}
          onCheckedChange={(checked) => {
            const value = checked === true;
            onChangeHandler({
              autoCreateTask: value,
              autoCreateTaskThresholds: value ? initThresholds() : null,
            });
          }}
          checked={!!props.input.autoCreateTask}
          aria-label="Auto-Create Asana Task"
        />
        <span className="min-w-0">
          <span className="text-foreground block text-sm font-medium">Auto-Create Asana Task</span>
          <span className="text-muted-foreground mt-0.5 block text-xs leading-5">
            Create a remediation task when findings meet any threshold below.
          </span>
        </span>
      </label>

      {props.input.autoCreateTask && (
        <div className="w-full space-y-4">
          {isProviderConnectedStatusLoading ? (
            <Skeleton className="h-[104px] w-full rounded-xl" />
          ) : isAsanaConnected ? null : (
            <div className="border-border bg-card w-full space-y-3 rounded-xl border p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
              <div>
                <p className="text-foreground text-sm font-medium">Connect Asana</p>
                <p className="text-muted-foreground mt-0.5 text-sm">
                  Auto-created tasks need an Asana connection before this job can run.
                </p>
              </div>
              <ConnectTaskManager
                skipProjectSelection
                providers={[RemoteTaskManagerProvider.ASANA]}
                taskManagerType={TaskManagerType.REMEDIATION}
              />
            </div>
          )}

          <section className="w-full min-w-0 space-y-2" aria-labelledby={riskHeadingId}>
            <h3 id={riskHeadingId} className="text-foreground text-sm font-semibold tracking-tight">
              Risk Thresholds
            </h3>
            <RiskThreshold
              docEvalTypeId={props.input.documentEvaluationTypeId}
              riskThresholds={props.input.autoCreateTaskThresholds || []}
              setRiskThreshold={(partial, p) => {
                const thresholds = [...(props.input.autoCreateTaskThresholds || [])];
                const idx = thresholds.findIndex((i) => i.policyId === p.id);

                if (idx >= 0) {
                  thresholds[idx] = {
                    ...thresholds[idx]!,
                    ...partial,
                  };
                } else {
                  thresholds.push({
                    policyId: p.id,
                    riskLevel: RiskLevel.LOW,
                    count: 0,
                    ...partial,
                  });
                }

                onChangeHandler({ autoCreateTaskThresholds: thresholds });
              }}
              onApplyToAll={({ riskLevel, count }) => {
                const next: RiskThresholdItem[] = [];

                evalType?.documentEvaluationTypePolicyGroup.forEach((i) => {
                  const policyGroup = allDocumentEvaluationTypes?.policyGroups.find(
                    (g) => g.id === i.policyGroupId,
                  );
                  policyGroup?.policies.forEach((p) => {
                    next.push({
                      policyId: p.id,
                      riskLevel,
                      count,
                    });
                  });
                });

                onChangeHandler({ autoCreateTaskThresholds: next });
              }}
              readOnly={!!props.isReadOnly}
            />
          </section>
        </div>
      )}
    </div>
  );
}
